import { ByteList } from 'byte-list';
import { DirectConnectBaseDevice, BLE_SERVICES } from '../base/directConnectBaseDevice';
import {
  DeviceTypeIds,
  RR3MessageType,
  TR4MessageType,
  TR4EMessageType,
  DeviceFamily,
  DeviceTypeHelper,
} from '@ncss/models/dist/v3';
import { shallowRef, ShallowRef } from 'vue';
import { BatteryChargeStatusType, DC301DeviceMessage, DC301DeviceMessageType } from './messages/dc301DeviceMessage';
import { DC301Flags } from './messages/dc301Flags';
import { BitwiseHelper, ConversionUtils, TR4EInfoMessageType } from '@ncss/models';
import { DC301InfoMessage, DC301InfoMessageType } from './messages/dc301InfoMessage';
import { EndDeviceMessage, EndDeviceMessageType } from './messages/endDeviceMessage';
import { EndDeviceInfoMessage } from './messages/endDeviceInfoMessage';
import { DirectConnectEndDevice } from './end-devices/base-end-device';
import { DirectConnectRR3 } from './end-devices/rr3';
import { DirectConnectTR4I } from './end-devices/tr4i';
import { DirectConnectTR4E } from './end-devices/tr4e';

const EndDeviceMessageTypes = [
  RR3MessageType.USER_EVENT,
  RR3MessageType.RESPONSE,
  TR4MessageType.USER_EVENT,
  TR4MessageType.RESPONSE,
  TR4EMessageType.USER_EVENT,
  TR4EMessageType.RESPONSE,
];

export class DirectConnectDC301 extends DirectConnectBaseDevice {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.DIRECT_CONNECT_PROGRAMMER, DirectConnectDC301);
  }

  protected serviceId = BLE_SERVICES.DIRECT_CONNECT.ID;
  protected notifyCharacteristicId = BLE_SERVICES.DIRECT_CONNECT.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.DIRECT_CONNECT.CHARACTERISTICS.WRITE;

  get hardwareVersion() {
    return `${this._hardwareVersion.value?.major || 0}.${this._hardwareVersion.value?.minor || 0}`;
  }

  get firmwareVersion() {
    return `${this._firmwareVersion.value?.major || 0}.${this._firmwareVersion.value?.minor || 0}`;
  }

  get batteryPercentage() {
    return `${this._batteryPercentage.value}%`;
  }

  get isCharging() {
    const status = this._batteryChargeStatus.value;
    return (
      status === BatteryChargeStatusType.BATTERY_CHARGING_CC ||
      status === BatteryChargeStatusType.BATTERY_CHARGING_CV ||
      status === BatteryChargeStatusType.BATTERY_CHARGE_TRICKLE
    );
  }

  get temperatureStr() {
    if (this.useMetric) {
      return this._temperature.value + ' C';
    }
    return (ConversionUtils.ConvertCelsiusToFahrenheit(this._temperature.value)?.toFixed(1) || 0) + ' F';
  }

  get name() {
    return this._name.value;
  }

  get loading() {
    return this._loading.value;
  }

  get partying() {
    return this._flags.value?.partyMode;
  }

  get visibleEndDevices() {
    return this._visibleEndDevices.value;
  }

  private _visibleEndDevices: ShallowRef<DirectConnectEndDevice[]> = shallowRef([]);
  private _loading: ShallowRef<boolean> = shallowRef(true);
  private _hardwareVersion: ShallowRef<{ major: number; minor: number } | undefined> = shallowRef();
  private _firmwareVersion: ShallowRef<{ major: number; minor: number } | undefined> = shallowRef();
  private _firmwareVersionBle: ShallowRef<{ major: number; minor: number } | undefined> = shallowRef();
  private _batteryPercentage: ShallowRef<number | undefined> = shallowRef();
  private _batteryChargeStatus: ShallowRef<BatteryChargeStatusType | undefined> = shallowRef();
  private _temperature: ShallowRef<number | undefined> = shallowRef();
  private _flags: ShallowRef<DC301Flags | undefined> = shallowRef();
  private _name: ShallowRef<string | undefined> = shallowRef();
  private _broadcastInterval: NodeJS.Timeout | undefined = undefined;

  protected parseManufacturerData(bytes: ByteList): void {
    const rawHardwareVersion = bytes.readByte();
    this._hardwareVersion.value = {
      minor: BitwiseHelper.GetBits(rawHardwareVersion, 0, 3),
      major: BitwiseHelper.GetBits(rawHardwareVersion, 3, 5),
    };
    this._firmwareVersion.value = {
      major: bytes.readByte(),
      minor: bytes.readByte(),
    };
    this._firmwareVersionBle.value = {
      major: bytes.readByte(),
      minor: bytes.readByte(),
    };
    this._batteryPercentage.value = bytes.readByte();
    this._batteryChargeStatus.value = bytes.readByte();
    this._temperature.value = bytes.readInt8();
    this._flags.value = new DC301Flags(bytes.readUInt32());
    bytes.readByte(); // Reserved 1 byte
    // Check to ensure that BLE properly sent over the scan response bytes
    // that contain the rest of the Device Name from the DC
    if (bytes.length - bytes.index < 26) {
      return;
    }
    bytes.readUInt16(); // Reserved Scan Response 2 bytes
    this._name.value = bytes.readString({ length: 24 });
  }

  protected onConnected(): void {
    super.onConnected();
    this.requestInfo();
    this._broadcastInterval = setInterval(() => {
      this.requestTR4EInfo();
    }, 10_000);
  }

  protected onDisconnected(): void {
    super.onDisconnected();
    if (this._broadcastInterval) {
      clearInterval(this._broadcastInterval);
      this._broadcastInterval = undefined;
    }
  }

  protected registerMessages(): void {
    this.messageService.registerMessageGroups([
      {
        mask: DC301DeviceMessageType.GROUP_MASK,
        messageClass: DC301DeviceMessage,
        expectedMessageVersion: 5,
        handler: this.onDC301DeviceMessage.bind(this),
      },
      {
        mask: EndDeviceMessageType.GROUP_MASK,
        messageClass: EndDeviceMessage,
        handler: this.onEndDeviceMessage.bind(this),
      },
    ]);
  }

  private requestInfo() {
    console.log('%c Requesting DC301 Info', 'color: cyan');
    const msg = new DC301InfoMessage(DC301InfoMessageType.REQUEST_INFO);
    return this.sendMessage<DC301DeviceMessage>(msg);
  }

  private onDC301DeviceMessage(msg: DC301DeviceMessage) {
    this._loading.value = false;
    console.log('%c DC301 Device Message', 'color: cyan', msg);
    this._hardwareVersion.value = {
      major: msg.hardwareVersionMajor,
      minor: msg.hardwareVersionMinor,
    };
    this._firmwareVersion.value = {
      major: msg.firmwareVersionMajor,
      minor: msg.firmwareVersionMinor,
    };
    this._firmwareVersionBle.value = {
      major: msg.bleFirmwareVersionMajor,
      minor: msg.bleFirmwareVersionMinor,
    };
    this._name.value = msg.deviceName;
    this._batteryPercentage.value = msg.batteryPercent;
    this._temperature.value = msg.temperature;
    this._batteryChargeStatus.value = msg.batteryChargeStatus;
    this._flags.value = new DC301Flags(msg.rawFlags);
    this.checkResponse(msg);
  }

  private onEndDeviceMessage(msg: EndDeviceMessage) {
    if (EndDeviceMessageTypes.includes(msg.msgType)) {
      const index = this.visibleEndDevices.findIndex((d) => d.deviceId === msg.srcAddr);
      if (index !== -1) {
        this.visibleEndDevices[index].updateFromEndDeviceMessage(msg);
      } else {
        const device = this.toVisibleEndDevice(msg);
        if (device) {
          this._visibleEndDevices.value = [...this._visibleEndDevices.value, device].sort(
            (a, b) => b.lastMsgAt.getTime() - a.lastMsgAt.getTime()
          );
        }
      }

      this.checkResponse(msg);
    }
  }

  private toVisibleEndDevice(msg: EndDeviceMessage): DirectConnectEndDevice | null {
    const deviceType = DeviceTypeHelper.GetDeviceTypeBySerialNumber(msg.srcAddr);
    if (deviceType?.family === DeviceFamily.REMOTE_READER) {
      return new DirectConnectRR3(msg, this.useMetric);
    }
    if (deviceType?.family === DeviceFamily.TRANSCEIVER) {
      if (deviceType && DeviceTypeHelper.GetIsEmbeddedTransceiver(deviceType!.id)) {
        return new DirectConnectTR4E(msg, this.useMetric);
      } else {
        return new DirectConnectTR4I(msg, this.useMetric);
      }
    }
    return null;
  }

  private requestTR4EInfo() {
    const msg = new EndDeviceInfoMessage(0xffffffff, TR4EInfoMessageType.DC301_REQUEST);
    return this.sendMessage<EndDeviceMessage>(msg);
  }

  setName(name: string): Promise<boolean> {
    const msg = new DC301InfoMessage(DC301InfoMessageType.SET_INFO);
    msg.setDeviceName = true;
    msg.deviceName = name;
    return this.sendMessage<DC301DeviceMessage>(msg, {
      match: (r) => {
        return r.type === DC301DeviceMessageType.DC_RESPONSE;
      },
    }).then((val) => {
      return !!val;
    });
  }

  factoryReset() {
    const msg = new DC301InfoMessage(DC301InfoMessageType.SET_INFO);
    msg.factoryReset = true;
    return this.sendMessage(msg);
  }

  setParty(party: boolean): Promise<boolean> {
    const msg = new DC301InfoMessage(DC301InfoMessageType.SET_INFO);
    msg.partyMode = party;
    return this.sendMessage<DC301DeviceMessage>(msg, {
      match: (res) => res.flags.partyMode === party,
    }).then((res) => !!res);
  }

  sendEndDeviceInfoMessage(
    msg: EndDeviceInfoMessage,
    waitForResponse?: { match: (res: EndDeviceMessage) => boolean; timeOutSeconds?: number; retries?: number }
  ) {
    console.log('%c sending EndDeviceInfoMessage', 'color: cyan', msg);
    return this.sendMessage(msg, waitForResponse);
  }

  forgetEndDevice(deviceId: number) {
    this._visibleEndDevices.value = this._visibleEndDevices.value.filter((d) => d.deviceId !== deviceId);
  }
}
